import { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Save } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/profile`, formData);
      updateUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="profile-title">My Profile</h1>
            <p className="text-lg text-slate-600">Manage your account details</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card className="p-8 border-slate-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-slate-900 mb-2 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10 py-6 rounded-lg border-slate-300"
                      required
                      data-testid="profile-name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-900 mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="pl-10 py-6 rounded-lg border-slate-300 bg-slate-100"
                      data-testid="profile-email-input"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-900 mb-2 block">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={user?.phone || ''}
                      disabled
                      className="pl-10 py-6 rounded-lg border-slate-300 bg-slate-100"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Phone cannot be changed</p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
                  data-testid="profile-save-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                  <Save className="ml-2 w-5 h-5" />
                </Button>
              </form>

              {/* Subscription Info */}
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h2 className="text-xl font-bold text-slate-900 mb-4">Subscription Status</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Plan:</span>
                    <span className="font-semibold text-slate-900">
                      {user?.subscription_status === 'active' 
                        ? user.subscription_plan?.toUpperCase() 
                        : 'No active subscription'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status:</span>
                    <span className={`font-semibold ${
                      user?.subscription_status === 'active' 
                        ? 'text-green-600' 
                        : 'text-slate-600'
                    }`}>
                      {user?.subscription_status || 'Inactive'}
                    </span>
                  </div>
                  {user?.subscription_end && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Valid Until:</span>
                      <span className="font-semibold text-slate-900">
                        {new Date(user.subscription_end).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
